import { useState } from "react";

/**
 * Reusable bulk-delete dialog used by Variations, Purchase Orders, Documents.
 * - Lists the N items (truncated at 20)
 * - If any Approved variations are selected, shows a financial-override acknowledgement
 * - Requires the user to type DELETE
 */
export default function BulkDeleteDialog({
  open, onClose, count, items, totalAmount, hasApproved,
  onConfirm, labels = { entity: "variations", noun: "variation" },
}) {
  const [typed, setTyped] = useState("");
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (!open) return null;

  const canConfirm = typed.trim() === "DELETE" && (!hasApproved || ack);

  async function go() {
    setBusy(true); setErr("");
    try {
      await onConfirm({ confirm_text: typed, financial_override: ack });
      setTyped(""); setAck(false);
      onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === "string" ? d : (d?.message || "Delete failed"));
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
         data-testid="bulk-delete-dialog">
      <div className="card max-w-lg w-full">
        <h3 className="text-silver mb-2">
          Delete {count} {count === 1 ? labels.noun : labels.entity}?
        </h3>
        <p className="text-sm opacity-80 mb-1">
          {count} {count === 1 ? labels.noun : labels.entity} will be moved to
          Deleted items (restorable for 30 days).
        </p>
        {typeof totalAmount === "number" && totalAmount > 0 && (
          <p className="text-sm text-silver mb-1" data-testid="bulk-delete-amount">
            Approved amount being removed:{" "}
            <b>${totalAmount.toLocaleString("en-NZ", { minimumFractionDigits: 2 })}</b> (ex GST).
          </p>
        )}
        {items && items.length > 0 && (
          <ul className="text-xs opacity-70 max-h-32 overflow-auto my-2 border border-neutral-800 rounded p-2"
              data-testid="bulk-delete-list">
            {items.slice(0, 20).map((it, i) => <li key={i}>• {it}</li>)}
            {items.length > 20 && <li>…and {items.length - 20} more</li>}
          </ul>
        )}
        {hasApproved && (
          <label className="flex items-start gap-2 text-sm mt-2 cursor-pointer"
                 data-testid="bulk-delete-override-label">
            <input type="checkbox" checked={ack} onChange={e => setAck(e.target.checked)}
                   data-testid="bulk-delete-override" className="mt-1"/>
            <span className="text-amber-200">
              I understand this removes financial records and changes the contract total.
            </span>
          </label>
        )}
        <label className="text-xs block mt-3">
          Type <b className="text-silver">DELETE</b> to confirm
          <input className="input mt-1" value={typed}
                 onChange={e => setTyped(e.target.value)}
                 data-testid="bulk-delete-typed" autoFocus/>
        </label>
        {err && (
          <div className="text-red-500 text-sm mt-2" data-testid="bulk-delete-error">{err}</div>
        )}
        <div className="flex gap-2 mt-4">
          <button
            className={`px-3 py-1.5 rounded text-sm ${canConfirm ? "bg-red-600 text-white" : "bg-neutral-700 text-neutral-400 cursor-not-allowed"}`}
            disabled={!canConfirm || busy}
            onClick={go} data-testid="bulk-delete-confirm">
            {busy ? "Deleting…" : `Delete ${count} ${count === 1 ? labels.noun : labels.entity}`}
          </button>
          <button className="btn-ghost" onClick={onClose} data-testid="bulk-delete-cancel">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
